import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Grid,
} from '@mui/material';
import {
  CalendarMonth as CalendarMonthIcon,
  Public as PublicIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import calendarApi, { Booking, BookingType } from './calendarApi';

const STATUS_COLORS: Record<Booking['status'], 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  confirmed: 'success',
  pending: 'warning',
  cancelled: 'error',
  completed: 'info',
  no_show: 'default',
};

const BookingsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [types, setTypes] = useState<BookingType[]>([]);

  const [from, setFrom] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().add(60, 'day').format('YYYY-MM-DD'));
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await calendarApi.listBookings({
        from: dayjs(from).toISOString(),
        to: dayjs(to).endOf('day').toISOString(),
        status: statusFilter || undefined,
        typeId: typeFilter || undefined,
        source: sourceFilter || undefined,
      });
      setBookings(list);
    } catch (e: any) {
      setError(e.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [from, to, statusFilter, typeFilter, sourceFilter]);

  useEffect(() => { calendarApi.getBookingTypes().then(setTypes).catch(() => setTypes([])); }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return bookings;
    return bookings.filter((b) =>
      b.title?.toLowerCase().includes(s) ||
      b.customer.name?.toLowerCase().includes(s) ||
      b.customer.email?.toLowerCase().includes(s) ||
      b.customer.company?.toLowerCase().includes(s)
    );
  }, [bookings, search]);

  const stats = useMemo(() => {
    const total = bookings.length;
    const upcoming = bookings.filter((b) => dayjs(b.startAt).isAfter(dayjs()) && b.status !== 'cancelled').length;
    const fromPublic = bookings.filter((b) => b.source === 'public').length;
    const cancelled = bookings.filter((b) => b.status === 'cancelled').length;
    return { total, upcoming, fromPublic, cancelled };
  }, [bookings]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <PageHeader
        title="Bookings"
        subtitle="Searchable list of every booking across all calendars"
        action={
          <Stack direction="row" spacing={1}>
            <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
            <Chip
              icon={<CalendarMonthIcon />}
              label="Open calendar"
              clickable
              onClick={() => navigate('/admin/calendar')}
            />
          </Stack>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'Total bookings', value: stats.total, color: '#2563eb' },
          { label: 'Upcoming', value: stats.upcoming, color: '#22c55e' },
          { label: 'From public page', value: stats.fromPublic, color: '#0ea5e9' },
          { label: 'Cancelled', value: stats.cancelled, color: '#ef4444' },
        ].map((s) => (
          <Grid item xs={6} md={3} key={s.label}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: s.color }}>{s.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <TextField label="From" type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="To" type="date" size="small" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField select label="Status" size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="">All</MenuItem>
              {['confirmed', 'pending', 'cancelled', 'completed', 'no_show'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField select label="Type" size="small" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All</MenuItem>
              {types.map((t) => <MenuItem key={t.type_id} value={t.type_id}>{t.name}</MenuItem>)}
            </TextField>
            <TextField select label="Source" size="small" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="internal">Internal</MenuItem>
              <MenuItem value="public">Public</MenuItem>
            </TextField>
            <TextField label="Search" size="small" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1 }} />
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 5, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell width={50} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No bookings in this range</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((b) => (
                  <TableRow key={b.bookingId} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/calendar?booking=${b.bookingId}`)}>
                    <TableCell>
                      <Typography variant="body2">{dayjs(b.startAt).format('DD MMM YYYY')}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(b.startAt).format('HH:mm')} – {dayjs(b.endAt).format('HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>{b.title}</TableCell>
                    <TableCell>
                      {b.typeName && (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: b.typeColor || '#cbd5e1' }} />
                          <Typography variant="body2">{b.typeName}</Typography>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      {b.customer.name && (
                        <>
                          <Typography variant="body2">{b.customer.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{b.customer.email}</Typography>
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      {b.source === 'public'
                        ? <Chip size="small" icon={<PublicIcon />} label="Public" color="info" />
                        : <Chip size="small" label="Internal" variant="outlined" />}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={b.status} color={STATUS_COLORS[b.status]} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small"><VisibilityIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default BookingsListPage;
